import React, {ReactElement, useEffect, useState, useCallback, useRef} from 'react';
import {
  Button as MUIButton,
  Card,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import api from '../../services/api';
import { toaster } from '../../services/toaster';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import { custom } from '../../schema/custom';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import { SchemaWithClient } from '../../types/SchemaWithClient';
import { Entity } from '../../types/entities/Entity';
import { SlidingPaneEdit } from '../Page/SlidingPaneEdit';
import { SlidingPaneAddNew } from '../Page/SlidingPaneAddNew';
import CardContent from '@mui/material/CardContent';

import AbcRoundedIcon from '@mui/icons-material/AbcRounded';
import PinOutlinedIcon from '@mui/icons-material/PinOutlined';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';
import FormatColorTextRoundedIcon from '@mui/icons-material/FormatColorTextRounded';
import DriveEtaRoundedIcon from '@mui/icons-material/DriveEtaRounded';
import {LoadingButton} from "@mui/lab";

type Props<T extends Entity> = {
  schema: SchemaWithClient<T>;
};

export const ServiceProviderCustomFields = ({
  schema,
  values,
  onChange,
}: {
  schema: any;
  values: any;
  onChange?: (value: any, column: string) => void;
}): ReactElement => {
  const [openedSlidingPane, setOpenedSlidingPane] = useState<any>({});
  const [loadingButton, setLoadingButton] = useState<any>(false);

  const [currentAddNewPaneEntity, setCurrentAddNewPaneEntity] = useState<
    any
  >({
    schema: schema,
    row: {},
    key: undefined,
  });
  const [openConfirmDialog, setOpenConfirmDialog] = useState<any>(false);
  const [properties, setProperties] = useState<any>(undefined);
  const [loading, setLoading] = useState<any>(false);

  const [query, setQuery] = useState<'idle' | 'progress' | 'success'>(
    'idle'
  );
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const fetchColumns = useCallback(() => {
    setLoading(true);
    const searchParams = new URLSearchParams({
      service_provider: values?.id,
    });

    const prices = api.get('/custom-fields', searchParams).then((res: any) => {
      setProperties(res?.data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    fetchColumns();
  }, []);

  return (
    <>
      {loading ? (
        <>
          <Skeleton variant="rectangular" width={'100%'} height={30} />
          <Skeleton
            variant="rectangular"
            width={'100%'}
            height={100}
            sx={{
              marginTop: '10px',
            }}
          />
        </>
      ) : (
        <>
          <Stack direction={'column'} gap={2} alignItems={'flex-start'}>
            <Stack direction={'row'} gap={2}>
              {properties && (
                <>
                  {properties.map((item: any) => {
                    return (
                      <Card
                        variant="outlined"
                        sx={{
                          width: '100%',
                        }}
                      >
                        <CardContent>
                          <Typography variant="h5" component="div">
                            {item?.name}
                          </Typography>
                          <Typography sx={{}} color="text.secondary">
                            {item.type === 'string' && <AbcRoundedIcon />}
                            {(item.type === 'number' ||
                              item.type === 'float') && <PinOutlinedIcon />}
                            {item.type === 'boolean' && (
                              <CheckBoxOutlinedIcon />
                            )}
                            {item.type === 'text' && (
                              <FormatColorTextRoundedIcon />
                            )}
                            {item.type === 'driver' && <DriveEtaRoundedIcon />}
                            <Typography variant="subtitle2" component="span">
                              ({item?.type})
                            </Typography>
                          </Typography>
                          <Typography sx={{}} color="text.secondary">
                            {`Created: ${item?.created_at}`}
                          </Typography>
                        </CardContent>
                      </Card>
                    );
                  })}
                </>
              )}
            </Stack>
            <Stack>
              <Tooltip
                title={`Add new Custom Field`}
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -14],
                        },
                      },
                    ],
                  },
                }}
              >
                <IconButton
                  color={'primary'}
                  onClick={() => {
                    setCurrentAddNewPaneEntity({
                      row: {
                        id: 'new',
                        service_provider: {
                          id: values?.id,
                          name: values?.name,
                        },
                      },
                      schema: custom,
                    });
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </>
      )}

      {openConfirmDialog && (
        <Dialog
          open={openConfirmDialog}
          onClose={() => {
            setOpenConfirmDialog(false);
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {'Are you sure you want to delete it?'}
          </DialogTitle>
          <DialogActions>
            <MUIButton
              onClick={() => {
                setOpenConfirmDialog(false);
              }}
            >
              Cancel
            </MUIButton>
            <LoadingButton
              loading={loadingButton}
              variant={'contained'}
              color={'error'}
              onClick={() => {
                setLoadingButton(true);
                const deletedRow = openConfirmDialog?.row;
                openConfirmDialog?.schema.client
                  .delete(deletedRow.id as number, { id: deletedRow.id })
                  .then((res: any) => {
                    const rows = [...properties[`${openConfirmDialog?.tab}`]];
                    const newRows = rows.filter(
                      (row: any) => row.id !== deletedRow.id
                    );
                    setProperties({
                      ...properties,
                      [`${openConfirmDialog?.tab}`]: newRows,
                    });
                    toaster.show({
                      intent: 'success',
                      message: `Successfully deleted ${schema.name}: ${
                        deletedRow?.name ? deletedRow?.name : deletedRow?.id
                      }`,
                    });
                    setOpenConfirmDialog(false);

                    setQuery('progress');
                    timerRef.current = setTimeout(() => {
                      setQuery('success');
                    }, 2000);
                  })
                  .catch((e: any) => {
                    toaster.show({
                      icon: 'error',
                      message: `Error: Unable to delete ${schema.name}: ${
                        deletedRow?.name ? deletedRow?.name : deletedRow?.id
                      }`,
                      intent: 'danger',
                    });
                  }).finally(()=>{
                  setLoadingButton(false);
                });
              }}
              autoFocus
            >
              Delete
            </LoadingButton>
          </DialogActions>
        </Dialog>
      )}

      {openedSlidingPane?.row?.id && (
        <SlidingPaneEdit
          schema={openedSlidingPane?.schema}
          id={openedSlidingPane?.row?.id}
          onSubmit={() => {
            console.log('adatbázis mentés a SLIDEPANEEEN');
          }}
          propertiesFromTable={
            openedSlidingPane?.row ? openedSlidingPane?.row : {}
          }
          onClose={(value) => {
            if (openedSlidingPane?.schema.name.toLowerCase() === 'services') {
              const tabDetails = [...properties[`${openedSlidingPane?.tab}`]];
              const newData = tabDetails.map((item: any) => {
                item.services = item.services.map((serviceItem: any) => {
                  return serviceItem?.id === value?.id ? value : serviceItem;
                });
                return item;
              });

              setOpenedSlidingPane({});
              setProperties({
                ...properties,
                [`${openedSlidingPane?.tab}`]: newData,
              });
              return true;
            }
            const tabDetails = [...properties[`${openedSlidingPane?.tab}`]];
            const newData = tabDetails.map((item: any) => {
              if (item?.id === value?.id) {
                return value;
              }
              return item;
            });
            setOpenedSlidingPane({});

            setProperties({
              ...properties,
              [`${openedSlidingPane?.tab}`]: newData,
            });
            setQuery('progress');
            timerRef.current = setTimeout(() => {
              setQuery('success');
            }, 2000);

            return true;
          }}
        />
      )}

      {currentAddNewPaneEntity?.row?.id && (
        <SlidingPaneAddNew
          schema={currentAddNewPaneEntity?.schema}
          mainSchema={schema}
          id={currentAddNewPaneEntity?.row?.id}
          hasSlidingPane={false}
          onSubmit={(result: any) => {
            fetchColumns();
            setCurrentAddNewPaneEntity({
              schema: schema,
              row: {},
              key: undefined,
            });

            setQuery('progress');
            timerRef.current = setTimeout(() => {
              setQuery('success');
            }, 2000);
          }}
          propertiesFromTable={
            currentAddNewPaneEntity?.row ? currentAddNewPaneEntity?.row : {}
          }
          row={currentAddNewPaneEntity?.row ? currentAddNewPaneEntity?.row : {}}
          onClose={(value) => {
            setCurrentAddNewPaneEntity({
              schema: schema,
              row: {},
              key: undefined,
            });
          }}
        />
      )}
    </>
  );
};
